import req from '@sportninja/common/api/request'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import React from 'react'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { registerError } from 'src/utils/sentry'
import { PAYMENT_PROVIDERS } from './usePaymentProvider'

const SportsPayFileName = __DEV__
  ? 'interpay_embed_test.min.js'
  : 'sportninja_v1.0.min.js'

const PAYMENT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  PENDING: 'pending',
  PROCESSING: 'processing',
}

/**
 * Loads the SportsPay script dynamically
 * @param {string} scriptUrl - The URL of the SportsPay script to load
 * @returns {Promise<void>} A promise that resolves when the script is loaded
 */
async function loadSportsPayScript(scriptUrl) {
  if (!scriptUrl) {
    throw new Error('Script URL is required to load SportsPay script')
  }

  // Check if script is already loaded
  if (document.querySelector(`script[src="${scriptUrl}"]`)) {
    return
  }

  const script = document.createElement('script')
  script.src = scriptUrl
  script.async = true

  // Create a promise to wait for script to load
  const scriptPromise = new Promise((resolve, reject) => {
    script.onload = resolve
    script.onerror = () => reject(new Error('Failed to load SportsPay script'))
  })

  document.head.appendChild(script)
  await scriptPromise
}

async function createPaymentIntent({ rootScheduleId, billingId }) {
  const clientSecret = await req(
    `/registration/${rootScheduleId}/payment/intent`,
    {
      method: 'POST',
      body: JSON.stringify({
        billing_schedule_id: billingId,
      }),
    }
  )
  return clientSecret
}

/**
 * Charges a payment for a registration, from the admin dashboard
 * @param {Object} params - The parameters for the chargePayment function
 * @param {string} params.rootScheduleId - The root schedule ID
 * @param {string} params.billingId - The billing ID
 * @returns {Promise<string>} The response from the API call
 * @throws {Error} If no Root Schedule ID or Billing ID is provided
 */
export async function chargePayment({ rootScheduleId, billingId }) {
  if (!rootScheduleId || !billingId) {
    throw new Error('No Root Schedule ID or Billing ID Provided')
  }
  const clientSecret = await req(
    `/registration/${rootScheduleId}/payment/charge`,
    {
      method: 'POST',
      body: JSON.stringify({
        billing_schedule_id: billingId,
      }),
    }
  )
  return clientSecret
}

/**
 * Charges a payment for a registration, coming from the home screen PAYMENT BANNER
 * @param {string} rootScheduleId - The root schedule ID
 * @param {string} billingId - The billing ID
 * @param {string} paymentProviderToken - The payment provider token
 * @param {string} [postalCode] - Billing ZIP; only collected for countries
 *   that require one (US today)
 * @param {string} [countryCode] - ISO 3166-1 alpha-2 code of the billing country
 * @returns {Promise<string>} The response from the API call
 * @throws {Error} If no Billing ID or Payment Provider Token is provided
 */
export async function chargeUserPayment({
  billingId,
  paymentProviderToken,
  postalCode,
  countryCode,
}) {
  if (!billingId || !paymentProviderToken) {
    throw new Error('No Billing ID or Payment Provider Token Provided')
  }
  const clientSecret = await req(`/users/me/billing/${billingId}/charge`, {
    method: 'POST',
    body: JSON.stringify({
      payment_provider_token: paymentProviderToken,
      // Only send what we actually have; omit the keys entirely otherwise
      ...(postalCode ? { postal_code: postalCode } : {}),
      ...(countryCode ? { country_code: countryCode } : {}),
    }),
  })
  return clientSecret
}

async function getDivision({ billingId }) {
  if (!billingId) {
    throw new Error('No Billing Id Provided')
  }

  const response = await req(`/users/me/billing/${billingId}`)
  const data = response?.data || response

  // Return the billing schedule object directly for consumers
  return data
}

/**
 * Full-balance ("Pay Full Balance") mode. Fetches the read-only consolidation preview for a
 * team registration, shaped as a 'full' billing row. NOTHING is mutated server-side here.
 * @param {Object} params
 * @param {string} params.teamRegistrationId - The team registration ID
 * @returns {Promise<Object>} The full-balance billing schedule preview
 */
async function getPayInFullPreview({ teamRegistrationId }) {
  if (!teamRegistrationId) {
    throw new Error('No Team Registration Id Provided')
  }

  const response = await req(
    `/billing/registration/${teamRegistrationId}/pay-in-full/preview`
  )
  const data = response?.data || response

  return data
}

/**
 * Creates the full-balance payment intent, keyed on the team registration. The amount is the
 * live consolidated total; nothing is mutated until the payment succeeds.
 * @param {Object} params
 * @param {string} params.rootScheduleId - The root schedule ID
 * @param {string} params.teamRegistrationId - The team registration ID
 * @returns {Promise<Object>} { client_secret, host_name }
 */
async function createPayInFullIntent({ rootScheduleId, teamRegistrationId }) {
  const clientSecret = await req(
    `/registration/${rootScheduleId}/payment/pay-in-full/intent`,
    {
      method: 'POST',
      body: JSON.stringify({
        team_registration_id: teamRegistrationId,
      }),
    }
  )
  return clientSecret
}

/**
 * SportsPay full-balance charge. On success the server consolidates the unpaid rows into a
 * single paid Full Payment row and emails the receipt; on failure nothing is mutated.
 * @param {Object} params
 * @param {string} params.rootScheduleId - The root schedule ID
 * @param {string} params.teamRegistrationId - The team registration ID
 * @param {string} params.paymentProviderToken - The tokenized card
 * @returns {Promise<Object>} The response from the API call
 * @throws {Error} If a required argument is missing
 */
export async function chargePayInFullBalance({
  rootScheduleId,
  teamRegistrationId,
  paymentProviderToken,
}) {
  if (!rootScheduleId || !teamRegistrationId || !paymentProviderToken) {
    throw new Error(
      'No Root Schedule ID, Team Registration ID or Payment Provider Token Provided'
    )
  }
  const clientSecret = await req(
    `/registration/${rootScheduleId}/payment/pay-in-full/charge`,
    {
      method: 'POST',
      body: JSON.stringify({
        team_registration_id: teamRegistrationId,
        payment_provider_token: paymentProviderToken,
      }),
    }
  )
  return clientSecret
}

const useSinglePaymentProvider = ({ billingId, teamRegistrationId }) => {
  const [state, setState] = useState({
    secret: null,
    billingSchedule: null,
    loading: false,
    error: null,
    paymentIsLoading: false,
    paymentStatus: null,
    paymentError: null,
    bootstrapFinished: false,
    sportsPayToken: null,
    paymentProvider: null,
    sportsPayHost: null,
  })

  const bootstrapCalled = React.useRef(false)

  /**
   * Effect hook that fetches billing schedule data and creates a payment intent when component mounts
   * or when any of the ID dependencies change.
   *
   * The flow:
   * 1. Validates that at least one ID is provided
   * 2. Sets loading state to true
   * 3. Fetches billing schedule data using the provided ID
   * 4. If no billing schedule is found, sets an error state
   * 5. Creates a payment intent using billing schedule data
   * 6. If no secret is generated, sets an error state
   * 7. Updates state with the fetched data and sets loading to false
   */
  useEffect(() => {
    const fetchDivisionAndCreatePaymentIntent = async () => {
      try {
        // Set loading state and clear error
        setState((prev) => ({
          ...prev,
          loading: true,
          error: null,
          paymentIsLoading: false,
          paymentStatus: null,
          paymentError: null,
        }))

        // Fetch billing schedule data. Full-balance mode reads the read-only consolidation
        // preview for the team registration; the legacy path reads a single billing row.
        const billingSchedule = teamRegistrationId
          ? await getPayInFullPreview({ teamRegistrationId })
          : await getDivision({ billingId })
        if (!billingSchedule) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: 'No Division Found',
          }))
          return
        }

        // Get payment provider from billing schedule data
        const paymentProvider =
          billingSchedule?.payment_provider_type?.id ||
          PAYMENT_PROVIDERS.SPORTS_PAY

        // Create payment intent using billing schedule data
        const { client_secret, host_name } = teamRegistrationId
          ? await createPayInFullIntent({
              rootScheduleId: billingSchedule?.root_schedule_id,
              teamRegistrationId,
            })
          : await createPaymentIntent({
              rootScheduleId: billingSchedule?.root_schedule_id,
              billingId: billingSchedule?.uid,
            })
        if (!client_secret) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: 'No Secret Found',
          }))
          return
        }

        // Update state with successful data
        setState((prev) => ({
          ...prev,
          secret: client_secret,
          sportsPayHost: host_name,
          billingSchedule,
          loading: false,
          paymentIsLoading: false,
          paymentStatus: null,
          paymentProvider,
        }))
      } catch (e) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e,
        }))
      }
    }

    // Exit early if no IDs are provided
    if (!billingId && !teamRegistrationId) {
      return
    }

    fetchDivisionAndCreatePaymentIntent()
  }, [billingId, teamRegistrationId])

  /**
   * Initializes the payment provider (Stripe or SportsPay) and sets up the payment form submission handler.
   *
   * The flow:
   * 1. Sets loading state to true and clears any previous errors
   * 2. Validates that required payment data exists
   * 3. Initializes the appropriate payment client based on provider
   * 4. Creates and mounts the payment element to the DOM
   * 5. Sets up an event listener for the payment form submission
   *
   * @returns {void}
   */
  useEffect(() => {
    // Only run bootstrap if we have the necessary data and haven't bootstrapped yet
    if (
      !state?.bootstrapFinished &&
      state?.secret &&
      state?.billingSchedule &&
      !bootstrapCalled.current
    ) {
      bootstrapCalled.current = true

      // Define the bootstrap function inside the effect to avoid dependency issues
      const runBootstrap = async () => {
        try {
          setState((prev) => ({
            ...prev,
            loading: true,
            error: null,
            bootstrapFinished: false,
          }))

          if (!state?.secret || !state?.billingSchedule) {
            return
          }

          if (state.paymentProvider === PAYMENT_PROVIDERS.STRIPE) {
            let stripeClient = null
            const host = window.location.hostname || window.location.host

            if (
              __DEV__ ||
              !['app.sportninja.com', 'canlanstats.sportninja.com'].includes(
                host
              )
            ) {
              stripeClient = await loadStripe(
                process.env.REACT_APP_STRIPE_KEY_DEV,
                {
                  stripeAccount: state?.billingSchedule?.connected_account,
                }
              )
            } else {
              stripeClient = await loadStripe(
                process.env.REACT_APP_STRIPE_KEY,
                {
                  stripeAccount: state?.billingSchedule?.connected_account,
                }
              )
            }

            if (!stripeClient) {
              setState((prev) => ({
                ...prev,
                loading: false,
                error: 'No Stripe Client Found',
              }))
              return
            }

            const elements = stripeClient.elements({
              clientSecret: state?.secret,
              appearance: {
                theme: 'night',
              },
            })

            const paymentElement = elements.create('payment', {
              paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
            })

            const paymentDiv = document.querySelector('#payment-element')

            if (!paymentDiv) {
              setState((prev) => ({
                ...prev,
                loading: false,
                error: 'No Payment Element Found',
              }))
              return
            }

            paymentElement.mount('#payment-element')

            const stripeForm = document.querySelector('#payment-form')

            stripeForm.addEventListener('submit', async (e) => {
              e.preventDefault()

              setState((prev) => ({
                ...prev,
                paymentIsLoading: true,
                paymentStatus: null,
                paymentError: null,
              }))

              if (!stripeClient) {
                setState((prev) => ({
                  ...prev,
                  paymentIsLoading: false,
                  paymentStatus: null,
                  paymentError: 'No Stripe Client Found',
                }))
                return
              }

              const { error } = await stripeClient.confirmPayment({
                elements,
                redirect: 'if_required',
              })

              if (error) {
                setState((prev) => ({
                  ...prev,
                  paymentIsLoading: false,
                  paymentStatus: PAYMENT_STATUS.FAILED,
                  paymentError: error.message,
                }))
              } else {
                setState((prev) => ({
                  ...prev,
                  paymentIsLoading: false,
                  paymentStatus: PAYMENT_STATUS.SUCCESS,
                }))
              }
            })
          } else if (state.paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY) {
            // Initialize SportsPay
            try {
              // Load SportsPay JS library
              const scriptUrl = `${state?.sportsPayHost}assets/${SportsPayFileName}`
              await loadSportsPayScript(scriptUrl)

              // Only update state if component is still mounted
              setState((prev) => ({
                ...prev,
                loading: false,
                error: null,
                bootstrapFinished: true,
              }))
            } catch (e) {
              const errorMessage = getErrorMessage(e)
              setState((prev) => ({
                ...prev,
                loading: false,
                error: errorMessage,
                bootstrapFinished: true,
              }))
            }
          }

          setState((prev) => ({
            ...prev,
            loading: false,
            error: null,
            bootstrapFinished: true,
          }))
        } catch (e) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: e,
          }))
        }
      }

      runBootstrap()
    }
  }, [
    state?.bootstrapFinished,
    state?.secret,
    state?.billingSchedule,
    state.paymentProvider,
    state?.sportsPayHost,
  ])

  /**
   * Validates credit card data with SportsPay
   * @param {Object} cardData - The credit card data to validate
   * @returns {Object} The validation result
   */
  const validateSportsPayCard = useCallback(
    (cardData) => {
      try {
        // eslint-disable-next-line no-undef
        const interpay = new InterPay()
        return interpay.validate(state?.secret, cardData)
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setState((prev) => ({
          ...prev,
          error: errorMessage,
        }))
        return { datavalid: false, text: errorMessage }
      }
    },
    [state?.secret]
  )

  /**
   * Tokenizes credit card data with SportsPay
   * @param {Object} cardData - The credit card data to tokenize
   * @returns {Promise<Object>} The tokenization result
   */
  const tokenizeSportsPayCard = useCallback(
    async (cardData) => {
      // eslint-disable-next-line no-undef
      const interpay = new InterPay()
      const result = await interpay.tokenize(
        state?.sportsPayHost.replace(/\/$/, ''),
        state?.secret,
        cardData
      )
      return result
    },
    [state?.sportsPayHost, state?.secret]
  )

  /**
   * Process payment with SportsPay
   * @param {Object} cardData - Credit card data to process
   * @param {Object} [billingAddress] - Billing address values from the card form
   * @param {string} [billingAddress.postalCode] - Billing ZIP (US only)
   * @param {string} [billingAddress.countryCode] - ISO 3166-1 alpha-2 code
   * @returns {Promise<boolean>} Whether the payment was successful
   */
  const processSportsPayPayment = useCallback(
    async (cardData, billingAddress = {}) => {
      if (!state.bootstrapFinished) {
        setState((prev) => ({
          ...prev,
          error: 'Sportspay Is Not Initialized',
        }))
        return false
      }

      setState((prev) => ({
        ...prev,
        paymentIsLoading: true,
        paymentStatus: PAYMENT_STATUS.PROCESSING,
      }))

      try {
        // First validate the card data
        const validationResult = validateSportsPayCard(cardData)

        if (!validationResult.datavalid) {
          throw new Error(validationResult.text || 'Invalid Card Data')
        }

        // Then tokenize the card
        const tokenResult = await tokenizeSportsPayCard(cardData)

        if (!tokenResult.datavalid || !tokenResult.ott) {
          throw new Error(tokenResult.text || 'Failed To Tokenize Card')
        }

        // Full-balance mode charges the team registration's live balance (server consolidates
        // only on success); the legacy path charges the single billing row.
        if (teamRegistrationId) {
          await chargePayInFullBalance({
            rootScheduleId: state?.billingSchedule?.root_schedule_id,
            teamRegistrationId,
            paymentProviderToken: tokenResult.ott,
          })
        } else {
          await chargeUserPayment({
            billingId: state?.billingSchedule?.uid || billingId,
            paymentProviderToken: tokenResult.ott,
            postalCode: billingAddress?.postalCode,
            countryCode: billingAddress?.countryCode,
          })
        }

        setState((prev) => ({
          ...prev,
          sportsPayToken: tokenResult.ott,
          paymentStatus: PAYMENT_STATUS.SUCCESS,
          paymentIsLoading: false,
        }))

        return true
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setState((prev) => ({
          ...prev,
          error: errorMessage,
          paymentStatus: PAYMENT_STATUS.FAILED,
          paymentIsLoading: false,
        }))

        registerError(e, 'Error processing SportsPay payment', {
          provider: state.paymentProvider,
        })

        return false
      }
    },
    [
      state.bootstrapFinished,
      state?.billingSchedule?.uid,
      state?.billingSchedule?.root_schedule_id,
      state.paymentProvider,
      validateSportsPayCard,
      tokenizeSportsPayCard,
      billingId,
      teamRegistrationId,
    ]
  )

  /**
   * Resets the bootstrap state to allow for retrying the bootstrap process.
   *
   * This function sets the bootstrapFinished state to false and resets the bootstrapCalled ref,
   * which triggers a re-evaluation of the component's dependencies and the bootstrap process.
   *
   * @returns {void}
   */
  const retryBootstrap = useCallback(() => {
    // Reset the bootstrapCalled ref so we can try bootstrapping again
    bootstrapCalled.current = false

    setState((prev) => ({
      ...prev,
      bootstrapFinished: false,
    }))
  }, [])

  const totalTaxSum = useMemo(() => {
    if (!state?.billingSchedule) {
      return 0
    }
    return state?.billingSchedule?.taxes?.reduce((acc, tax) => {
      return acc + Number(tax.tax_rate)
    }, 0)
  }, [state?.billingSchedule])

  return {
    ...state,
    retryBootstrap,
    totalTaxSum,
    validateSportsPayCard,
    tokenizeSportsPayCard,
    processSportsPayPayment,
  }
}

export default useSinglePaymentProvider
